import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { AUDIT_CALLING_DETAILS_REPORT, AUDIT_CALLING_REPORT } from "../../constants";
import axios from "axios";
import Loader from "../reusable Component/Loader";
import dayjs from "dayjs";
import FilterComponent from "./FilterComponent";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function CallingReportComponent({}) {
  const navigate = useNavigate();
  const empDetails = JSON.parse(localStorage.getItem("userData"));
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" or "desc"
  const [filters, setFilters] = useState({
    startDate: dayjs().subtract(7, "days").toDate(), // 7 days ago
    endDate: dayjs().toDate(), // Today
    agentNumber:
      localStorage.getItem("empRole") === "100" ? empDetails.EmployeePhone : "",
  });

  const [filterShow, setFilterShow] = useState(false);

  const [callStatistics, setCallStatistics] = useState([]);

  const [metricData, setMetricData] = useState([]);

  const fetchCallStatistics = () => {
    const { startDate, endDate, agentNumber } = filters;
    setLoading(true);

    // Determine yesterday's date
    const last7days = dayjs().subtract(6, "days").format("YYYY-MM-DD");

    // Format startDate and endDate, defaulting to yesterday if not provided
    const formattedStartDate = startDate
      ? dayjs(startDate).format("YYYY-MM-DD")
      : last7days;
    const formattedEndDate = endDate
      ? dayjs(endDate).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");

    // Build query params
    const queryParams = new URLSearchParams();
    queryParams.append("startDate", formattedStartDate);
    queryParams.append("endDate", formattedEndDate);
    

    axios
      .get(`${AUDIT_CALLING_REPORT}?${queryParams.toString()}`)
      .then((response) => {
        
        const { overallStats,employeeStats } = response.data;
        setCallStatistics(employeeStats);
        setMetricData(overallStats);
        
     
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setCallStatistics([]);
        setMetricData([]);
        setLoading(false);
      });
  };

  const location = useLocation();
  const prevFilters = location.state?.prevFilters;
  const [isInitialLoad, setIsInitialLoad] = useState(true);  // Flag to track initial load


  // Effect to set filters from prevFilters if available and avoid triggering API call on initial load
useEffect(() => {
  if (prevFilters && isInitialLoad) {
    // Set filters from prevFilters if available and avoid triggering API on initial load
    setFilters(prevFilters);
    setIsInitialLoad(false); // Mark as no longer initial load
  } else {
    // If no prevFilters, don't trigger the API call until filters are properly set
    setIsInitialLoad(false);
  }
}, [prevFilters, isInitialLoad]);

// Effect to trigger API call when filters are updated
useEffect(() => {
  if (!isInitialLoad && filters.startDate && filters.endDate) {
    // Call API only when filters are valid and set (and not on the initial load)
    fetchCallStatistics();
  }
}, [filters, isInitialLoad]);

  const handleNav = (agentName, agentNo) => {
    navigate(`/calling_report/call_details/${agentNo}/${agentName}`, {
      state: { prevFilters: filters },
    });
  };

  const handleViewClick = (agentName, agentNo) => {
    navigate(`/calling_report/call_details/${agentNo}/${agentName}`, {
      state: { prevFilters: filters },
    });
  };

  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortDirection === "asc";
    setSortColumn(column);
    setSortDirection(isAsc ? "desc" : "asc");
  };

  const sortedData = [...callStatistics].sort((a, b) => {
    if (!sortColumn) return 0;

    let valueA = a[sortColumn];
    let valueB = b[sortColumn];


    if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
    if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const handleExport = () => {
    // Define headers for export
    const headers = [
      { key: "Agent Name", value: "employeeName" },
      { key: "Total Calls", value: "total_calls" },
      { key: "Connected Calls", value: "connected_calls" },
      { key: "Missed Calls", value: "missed_calls" },
      { key: "Connection Rate", value: "connection_rate" },
    ];

    // Map data for export
    const exportData = sortedData.map((item) => ({
      "Agent Name": item.employeeName,
      "Total Calls": item.callStats?.totalCalls,
      "Connected Calls": item.callStats?.connectedCalls,
      "Missed Calls": item.callStats?.missedCalls,
      "Connected Time": item.callStats?.duration?.total,
    }));

    // Add total row to exportData
    const totalRow = {
      "Agent Name": "Total",
      "Total Calls": metricData.totalCalls || 0,
      "Connected Calls": metricData.totalConnectedCalls || 0,
      "Missed Calls": metricData.totalMissedCalls || 0,
    };
    exportData.push(totalRow);

    // Create worksheet from data
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add headers manually if necessary
    XLSX.utils.sheet_add_aoa(ws, [headers.map((header) => header.key)], {
      origin: "A1",
    });

    // Create a workbook and append worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Table Data");

    // Write workbook to file and download
    XLSX.writeFile(wb, "TableData.xlsx");
  };

  const defaultMetricData = {
    total_calls: 0,
    connected_calls: 0,
    missed_calls: 0,
    total_talk_time: 0,
    connection_rate: 0,
  };

  const metrics = metricData || defaultMetricData;

  
  return (
    <>
      {loading && <Loader open={loading} />}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, paddingLeft: "12px", flexGrow: 1 }}
        >
          Incoming Call Report
        </Typography>

        {/* IconButton aligned to the right */}
        <IconButton
          onClick={() => setFilterShow((prev) => !prev)}
          style={{
            border: "1px solid #fff", // Change border color to white for contrast
            borderRadius: "4px",
            margin: "5px",
            width: "40px", // Increased width for a more comfortable click area
            height: "40px", // Increased height for a more comfortable click area
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#097969",
            color: "#fff",
            transition: "background-color 0.3s, transform 0.2s", // Smooth transition effects
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)", // Add shadow for depth
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#0B9B73"; // Darker shade on hover
            e.currentTarget.style.transform = "scale(1.05)"; // Slightly scale up on hover
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#097969"; // Original color
            e.currentTarget.style.transform = "scale(1)"; // Reset scale
          }}
        >
          <FilterListIcon />
        </IconButton>

        {/* IconButton aligned to the right */}
        <IconButton
          onClick={handleExport}
          style={{
            border: "1px solid #fff", // Change border color to white for contrast
            borderRadius: "4px",
            margin: "5px",
            width: "40px", // Increased width for a more comfortable click area
            height: "40px", // Increased height for a more comfortable click area
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#097969",
            color: "#fff",
            transition: "background-color 0.3s, transform 0.2s", // Smooth transition effects
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)", // Add shadow for depth
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#0B9B73"; // Darker shade on hover
            e.currentTarget.style.transform = "scale(1.05)"; // Slightly scale up on hover
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#097969"; // Original color
            e.currentTarget.style.transform = "scale(1)"; // Reset scale
          }}
        >
          <FileDownloadIcon />
        </IconButton>
      </Box>

      {filterShow && (
        <FilterComponent
          filters={filters}
          setFilters={setFilters}
          data={callStatistics}
          setFilterShow={setFilterShow}
        ></FilterComponent>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        gap="16px"
        sx={{
          padding: "16px",
          backgroundColor: "#EEF0F4",
          borderRadius: "8px",
          marginBottom: "16px",
          marginTop: '8px'
        }}
      >
        {/* Total Calls */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "8px",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            boxShadow: 2,
            width: "30%",
          }}
        >
          <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
            Total Calls
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#333333" }}
          >
            {metrics.totalCalls}
          </Typography>
        </Box>

        {/* Connected Calls */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "8px",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            boxShadow: 2,
            width: "30%",
          }}
        >
          <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
            Connected Calls
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#333333" }}
          >
            {metrics.totalConnectedCalls}
          </Typography>
        </Box>

        {/* Missed Calls */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "8px",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            boxShadow: 2,
            width: "30%",
          }}
        >
          <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
            Missed Calls
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#333333" }}
          >
            {metrics.totalMissedCalls}
          </Typography>
        </Box>

        {/* Total Talk time */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "8px",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            boxShadow: 2,
            width: "30%",
          }}
        >
          <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
            Total Connected Time
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#333333" }}
          >
            {metrics.total_talk_time}
          </Typography>
        </Box>

        
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: "#EEF0F4" }}>
                <TableSortLabel
                  active={sortColumn === "agent_name"}
                  direction={sortDirection}
                  onClick={() => handleSort("agent_name")}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Agent Name
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ backgroundColor: "#EEF0F4" }}>
                <TableSortLabel
                  active={sortColumn === "total_calls"}
                  direction={sortDirection}
                  onClick={() => handleSort("total_calls")}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Total
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ backgroundColor: "#EEF0F4" }}>
                <TableSortLabel
                  active={sortColumn === "connected_calls"}
                  direction={sortDirection}
                  onClick={() => handleSort("connected_calls")}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Connected
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ backgroundColor: "#EEF0F4" }}>
                <TableSortLabel
                  active={sortColumn === "missed_calls"}
                  direction={sortDirection}
                  onClick={() => handleSort("missed_calls")}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Missed
                  </Typography>
                </TableSortLabel>
              </TableCell>
              
              <TableCell align="center" sx={{ backgroundColor: "#EEF0F4" }}>
                <TableSortLabel
                  active={sortColumn === "total_talk_time"}
                  direction={sortDirection}
                  onClick={() => handleSort("total_talk_time")}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Total Connected Time
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ backgroundColor: "#EEF0F4" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Action
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Total Row */}

            <TableRow>
              <TableCell
                sx={{ fontWeight: "bold", backgroundColor: "#EEF0F4" }}
              >
                Total
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bold", backgroundColor: "#EEF0F4" }}
              >
                {metrics.totalCalls}
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bold", backgroundColor: "#EEF0F4" }}
              >
                {metrics.totalConnectedCalls}
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bold", backgroundColor: "#EEF0F4" }}
              >
                {metrics.totalMissedCalls}
              </TableCell>
              
              <TableCell
                align="center"
                sx={{ fontWeight: "bold", backgroundColor: "#EEF0F4" }}
              >
                {metrics.total_talk_time}
              </TableCell>

              <TableCell
                align="center"
                sx={{ backgroundColor: "#EEF0F4" }}
              ></TableCell>
            </TableRow>

            {Array.isArray(sortedData) && sortedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>No Data available Yet</TableCell>
              </TableRow>
            ) : ( 
              sortedData?.map((item) => {
                return (
                  <TableRow key={item.employeeName}>
                    <TableCell sx={{ backgroundColor: "#EEF0F4" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() => handleNav(item.employeeName, item.employeePhone)}
                      >
                        {item.employeeName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{item.callStats?.totalCalls}</TableCell>
                    <TableCell align="center">{item.callStats?.connectedCalls}</TableCell>
                    <TableCell align="center">{item.callStats?.missedCalls}</TableCell>
                    <TableCell align="center">{item.callStats?.duration?.total}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View" arrow>
                        <IconButton
                          style={{
                            border: "1px solid",
                            borderRadius: "4px",
                            padding: "2px",
                            width: "24px",
                            height: "24px",
                            backgroundColor: "#00A36C",
                          }}
                          onClick={() => handleViewClick(item.employeeName, item.employeePhone)}
                        >
                          <VisibilityIcon
                            sx={{ fontSize: "1rem", color: "#fff" }}
                          />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default CallingReportComponent;
